import { useState, useMemo, useRef } from 'react'
import {
  useProductBrands,
  useCreateProductBrand,
  useUpdateProductBrand,
  useDeleteProductBrand,
  useUploadFile,
} from '@/admin/hooks/use-products'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Plus, Pencil, Trash2, Upload, X, Tag } from 'lucide-react'
import { toast } from 'sonner'
import type { ProductBrand } from '@/admin/types/products'
import { cn } from '@/shared/lib/utils'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { toastApiError } from '@/shared/lib/api-error'

export function ProductBrandsPage() {
  const { data: brands, isLoading } = useProductBrands()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingBrand, setEditingBrand] = useState<ProductBrand | null>(null)

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <span className="clara-section-label">Brands</span>
        <Button
          data-testid="brands-add-btn"
          onClick={() => setShowAddDialog(true)}
          className="flex cursor-pointer items-center gap-1.5 rounded-[4px] bg-[var(--green)] px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3.5 w-3.5" />
          Add brand
        </Button>
      </div>
      {isLoading ? (
        <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
      ) : !brands?.length ? (
        <p className="text-[13px] text-[var(--ink-3)]">
          No brands configured yet - add manufacturer brands to tag your products with.
        </p>
      ) : (
        <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
          {brands.map((brand, idx) => (
            <BrandRow
              key={brand.uuid}
              brand={brand}
              isLast={idx === brands.length - 1}
              onEdit={setEditingBrand}
            />
          ))}
        </div>
      )}
      <BrandDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
      {editingBrand && (
        <BrandDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditingBrand(null)
          }}
          existingBrand={editingBrand}
        />
      )}
    </div>
  )
}

function BrandRow({
  brand,
  isLast,
  onEdit,
}: {
  brand: ProductBrand
  isLast: boolean
  onEdit: (b: ProductBrand) => void
}) {
  const deleteBrand = useDeleteProductBrand()
  const [deleteOpen, setDeleteOpen] = useState(false)

  async function handleDelete() {
    try {
      await deleteBrand.mutateAsync(brand.uuid)
      toast.success('Brand deleted')
      setDeleteOpen(false)
    } catch (err) {
      toastApiError(err, 'Failed to delete brand')
    }
  }

  return (
    <>
      <div
        data-testid={`brands-item-${brand.uuid}`}
        className={cn(
          'group flex items-center gap-3 px-4 py-3 transition-all duration-100 hover:bg-[var(--bg-subtle)]',
          !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
        )}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
          {brand.logo ? (
            <img
              src={brand.logo.file_url}
              alt={brand.name}
              className="h-full w-full object-contain p-0.5"
            />
          ) : (
            <Tag className="h-4 w-4 text-[var(--ink-3)]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <span className="block text-[13px] text-[var(--ink)]">
            {brand.name}
          </span>
          {brand.description && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {brand.description}
            </span>
          )}
        </div>
        {brand.website && (
          <a
            href={brand.website}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] sm:block"
          >
            {brand.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        )}
        <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Button
            variant="ghost"
            size="icon-xs"
            data-testid={`brands-edit-${brand.uuid}`}
            onClick={() => onEdit(brand)}
            className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            data-testid={`brands-delete-${brand.uuid}`}
            onClick={() => setDeleteOpen(true)}
            className="h-6 w-6 cursor-pointer text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${brand.name}?`}
        description={
          <>
            Delete <strong>{brand.name}</strong>? This action cannot be undone.
          </>
        }
        onConfirm={handleDelete}
        isPending={deleteBrand.isPending}
      />
    </>
  )
}

function BrandDialog({
  open,
  onOpenChange,
  existingBrand,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  existingBrand?: ProductBrand
}) {
  const createBrand = useCreateProductBrand()
  const updateBrand = useUpdateProductBrand()
  const uploadFile = useUploadFile()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isEdit = !!existingBrand
  const [logoUuid, setLogoUuid] = useState<string | null>(
    existingBrand?.logo?.uuid ?? null,
  )
  const [logoPreview, setLogoPreview] = useState<string | null>(
    existingBrand?.logo?.file_url ?? null,
  )
  const [logoUploading, setLogoUploading] = useState(false)

  async function handleLogoSelect(file: File) {
    setLogoUploading(true)
    try {
      const uploaded = await uploadFile.mutateAsync({ file, use_case: 'BRAND_LOGO' })
      setLogoUuid(uploaded.uuid)
      setLogoPreview(uploaded.file_url)
    } catch (err) {
      toastApiError(err, 'Failed to upload logo')
    } finally {
      setLogoUploading(false)
    }
  }

  function clearLogo() {
    setLogoUuid(null)
    setLogoPreview(null)
  }

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        placeholder: 'Pentair',
        value: existingBrand?.name ?? '',
        autoFocus: true,
        required: 'Name is required',
        onChange: (value, formContext) => {
          if (!isEdit) {
            formContext?.setFieldValue(
              'slug',
              String(value ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, ''),
            )
          }
        },
      },
      {
        type: 'text',
        name: 'slug',
        label: 'Slug',
        placeholder: 'pentair',
        value: existingBrand?.slug ?? '',
        required: 'Slug is required',
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Pool & spa equipment manufacturer',
        value: existingBrand?.description ?? '',
      },
      {
        type: 'text',
        name: 'website',
        label: 'Website',
        placeholder: 'https://…',
        value: existingBrand?.website ?? '',
      },
    ],
    [
      existingBrand?.description,
      existingBrand?.name,
      existingBrand?.slug,
      existingBrand?.website,
      isEdit,
    ],
  )
  const { Form, ref } = useForm(
    {
      name: `product-brand-dialog-${existingBrand?.uuid ?? 'new'}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      const payload = {
        name: values.name.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        website: values.website.trim(),
        logo_uuid: logoUuid,
      }
      try {
        if (isEdit && existingBrand) {
          await updateBrand.mutateAsync({ uuid: existingBrand.uuid, ...payload })
          toast.success('Brand updated')
        } else {
          await createBrand.mutateAsync(payload)
          toast.success('Brand created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, isEdit ? 'Failed to update brand' : 'Failed to create brand')
      }
    },
    [existingBrand?.uuid ?? 'new', open, logoUuid, fields],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          ref.current?.resetForm({
            values: {
              name: existingBrand?.name ?? '',
              slug: existingBrand?.slug ?? '',
              description: existingBrand?.description ?? '',
              website: existingBrand?.website ?? '',
            },
          })
          setLogoUuid(existingBrand?.logo?.uuid ?? null)
          setLogoPreview(existingBrand?.logo?.file_url ?? null)
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit brand' : 'Add brand'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          {/* Logo uploader */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Logo
            </Label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="logo preview"
                    className="h-full w-full object-contain p-1"
                  />
                ) : (
                  <Tag className="h-5 w-5 text-[var(--ink-3)]" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void handleLogoSelect(f)
                    e.target.value = ''
                  }}
                />
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={logoUploading}
                    className="cursor-pointer"
                  >
                    <Upload className="mr-1 h-3 w-3" />
                    {logoUploading ? 'Uploading…' : logoPreview ? 'Replace' : 'Upload'}
                  </Button>
                  {logoPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={clearLogo}
                      className="cursor-pointer"
                    >
                      <X className="mr-1 h-3 w-3" />
                      Remove
                    </Button>
                  )}
                </div>
                <span className="text-[11px] text-[var(--ink-3)]">
                  PNG or SVG, max ~2MB
                </span>
              </div>
            </div>
          </div>
          <Form className="flex flex-col gap-4" />
        </div>
        <DialogFooter>
          <Button
            type="submit"
            form={`product-brand-dialog-${existingBrand?.uuid ?? 'new'}`}
            data-testid="brands-dialog-submit"
            disabled={createBrand.isPending || updateBrand.isPending || logoUploading}
            className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
